// Accounting › Reports (Blueprint's; pages/ux-full accounting.js, Reports), its own module (BL-189):
// PageHead with the reports this person may read as its tabs, the open one's name as the title, then that
// report as a Section. Its tools sit at the end of the Section's name line (the month a step at a time,
// Segmented's choices, a SelectField), with Download CSV a Link; a download that fails says why in red
// under the name line. Each report's table is a DataTable, its total the foot. AR aging's ages are the
// catalogued Aging, fed the report's own ages, then the customers' table. Loading is skeleton rows; a
// report that didn't load says so in red with Try again. Riverstone keeps Reports.tsx; reports-page.css
// lays out the tools and the muted lines, tokens only.
import {
  Aging,
  Button,
  DataTable,
  IconButton,
  Link,
  Notice,
  Segmented,
  SelectField,
  Skeleton,
  SkeletonGroup,
  type AgingAge,
  type DataColumn,
} from '@flowology/ui-web/fl';
import { PageHead, Section } from '@flowology/ui-web/patterns';
import { useState, type ReactNode } from 'react';
import { client } from '../api.ts';
import { officeHost } from '../blueprint/host.ts';
import { dayText, money, monthName, monthOf, todayIn } from '../books/model.ts';
import { errorText, useQuery } from '../books/ui.tsx';
import { stepMonth } from '../huddle/huddle-model.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { CHANNEL_WORDS, type Channel } from '../settings/campaigns-model.ts';
import { BOOKS_INCOME, REVENUE_WORDS } from '../shared/revenue-words.ts';
import {
  CAMPAIGN_GROUPS,
  campaignRowLabel,
  download,
  hoursText,
  monthWords,
  rateText,
  reportsFor,
  roasText,
  yearOf,
  type CampaignGroup,
  type Report,
} from './reports-model.ts';
import './reports-page.css';

const host = officeHost(() => undefined);

/** A figure, or the screen's nothing. */
const dash = (cents: number | null | undefined, shown = !!cents): string =>
  cents == null || !shown ? '—' : money(cents);

/** A report still loading (skeleton rows), or one that didn't load (a red sentence and Try again). */
function Waiting({ error, what, onRetry }: { error: string | null; what: string; onRetry: () => void }) {
  return error ? (
    <p className="bp-reports-bad" role="alert">
      <span>{error}</span>
      <Link onPress={onRetry}>Try again</Link>
    </p>
  ) : (
    <SkeletonGroup label={`Loading ${what}`}>
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} shape="row" width="full" />
      ))}
    </SkeletonGroup>
  );
}

/** A muted sentence: nothing to show, or what the report counts. */
const Say = ({ children }: { children: ReactNode }) => <p className="bp-reports-say">{children}</p>;

/**
 * "Download CSV": the same report again, with its file. A download that fails says why in red under the
 * Section's name line, for as long as the report shows the same input.
 */
function useCsv(action: string, input: object) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<{ key: string; text: string } | null>(null);
  const key = JSON.stringify(input);
  const link = (
    <Link
      isDisabled={busy}
      onPress={() => {
        setBusy(true);
        setProblem(null);
        void client
          .call<{ file?: { fileName: string; csv: string } }>(action, { ...input, csv: true })
          .then((r) => r.file && download(r.file))
          .catch((e: unknown) => setProblem({ key, text: errorText(e) }))
          .finally(() => setBusy(false));
      }}
    >
      Download CSV
    </Link>
  );
  const said =
    problem && problem.key === key ? (
      <p className="bp-reports-bad" role="alert">
        <span>{problem.text}</span>
      </p>
    ) : null;
  return { link, said };
}

/** The month a range report covers, stepped a month at a time: ‹ October, the month, December ›. */
function useMonth(tz: string) {
  const [[from, to], setRange] = useState(monthOf(todayIn(tz)));
  const before = stepMonth(from, -1);
  const after = stepMonth(from, 1);
  const picker = (
    <div className="bp-reports-step" role="group" aria-label="Month">
      <Button size="sm" onPress={() => setRange(before)}>
        {`‹ ${monthName(before[0]).split(' ')[0]}`}
      </Button>
      <span className="bp-reports-now">{monthName(from)}</span>
      <Button size="sm" onPress={() => setRange(after)}>
        {`${monthName(after[0]).split(' ')[0]} ›`}
      </Button>
    </div>
  );
  return { from, to, picker };
}

interface Props {
  tz: string;
}

interface TypeRow {
  label: string;
  count: number;
  receivedCents: number;
  refundedCents: number;
  netCents: number;
}
type PaymentsRow = TypeRow & { id: string; total?: boolean };
const PAYMENT_COLUMNS: DataColumn<PaymentsRow>[] = [
  { id: 'type', label: 'Type', rowHeader: true, cell: (r) => ({ text: r.label }) },
  { id: 'count', label: 'Payments', align: 'end', cell: (r) => ({ text: r.count }) },
  { id: 'received', label: 'Received', align: 'end', cell: (r) => ({ text: money(r.receivedCents) }) },
  {
    id: 'refunded',
    label: 'Refunded',
    align: 'end',
    // money out; the total says $0.00 where a row says nothing
    cell: (r) => ({ text: r.total ? money(-r.refundedCents) : dash(-r.refundedCents) }),
  },
  { id: 'net', label: 'Net', align: 'end', cell: (r) => ({ text: money(r.netCents) }) },
];

function Payments({ tz }: Props) {
  const m = useMonth(tz);
  const input = { from: m.from, to: m.to };
  const q = useQuery<{ rows: TypeRow[]; total: Omit<TypeRow, 'label'> }>('reports.paymentTypes', input);
  const csv = useCsv('reports.paymentTypes', input);
  return (
    <Section title={`Payments by type · ${monthName(m.from)}`}>
      <div className="bp-reports-tools">
        {m.picker}
        {csv.link}
      </div>
      {csv.said}
      {!q.data ? (
        <Waiting error={q.error} what="the payments" onRetry={() => void q.reload()} />
      ) : !q.data.rows.length ? (
        <Say>No payments this month.</Say>
      ) : (
        <DataTable
          label={`Payments by type, ${monthName(m.from)}`}
          columns={PAYMENT_COLUMNS}
          rows={q.data.rows.map((r) => ({ ...r, id: r.label }))}
          foot={{ ...q.data.total, id: 'total', label: 'Total', total: true }}
        />
      )}
    </Section>
  );
}

interface AgingAnswer {
  title: string;
  basis: 'invoice' | 'due';
  asOf: string;
  buckets: { key: string; label: string; count: number; cents: number }[];
  customers: {
    customerId: string;
    customerName: string;
    cents: Record<string, number>;
    totalCents: number;
  }[];
  totalCents: number;
}
type CustomerRow = { id: string; name: string; cents: Record<string, number>; totalCents: number };

/** The report's ages as Aging draws them: by due date, those past due are late, and over 30 days red. */
const agesOf = (a: AgingAnswer): AgingAge[] =>
  a.buckets.map((b) => ({
    id: b.key,
    name: b.label,
    cents: b.cents,
    count: b.count,
    ...(a.basis === 'due' && b.key !== 'current' && b.key !== 'undated'
      ? { late: true, bad: b.key !== 'd0_30' }
      : {}),
  }));

function AgingReport({ tz }: Props) {
  const [basis, setBasis] = useState<'invoice' | 'due'>('invoice');
  const asOf = todayIn(tz);
  const input = { asOf, basis };
  const q = useQuery<AgingAnswer>('reports.arAging', input);
  const csv = useCsv('reports.arAging', input);
  const d = q.data;
  const columns: DataColumn<CustomerRow>[] = [
    { id: 'customer', label: 'Customer', rowHeader: true, cell: (r) => ({ text: r.name }) },
    ...(d?.buckets ?? []).map((b): DataColumn<CustomerRow> => ({
      id: b.key,
      label: b.label,
      align: 'end',
      cell: (r) => ({ text: r.id === 'total' ? money(r.cents[b.key] ?? 0) : dash(r.cents[b.key]) }),
    })),
    { id: 'total', label: 'Total', align: 'end', cell: (r) => ({ text: money(r.totalCents) }) },
  ];
  return (
    <Section title={d?.title ?? 'AR aging'}>
      <div className="bp-reports-tools">
        <Segmented
          label="Aged by"
          items={[
            { id: 'invoice', label: 'By invoice date' },
            { id: 'due', label: 'By due date' },
          ]}
          selectedKey={basis}
          onSelectionChange={(b) => setBasis(b === 'due' ? 'due' : 'invoice')}
        />
        {csv.link}
      </div>
      {csv.said}
      {!d ? (
        <Waiting error={q.error} what="the aging" onRetry={() => void q.reload()} />
      ) : (
        <>
          <Aging
            data={{
              asOf: d.asOf,
              rows: [],
              buckets: { current: 0, days_1_30: 0, days_31_60: 0, days_61_90: 0, days_over_90: 0 },
              openInvoicesCents: d.totalCents,
              unappliedCreditsCents: 0,
            }}
            host={host}
            ages={agesOf(d)}
          />
          {!d.customers.length ? (
            <Say>Nothing is owed.</Say>
          ) : (
            <DataTable
              label={d.title}
              columns={columns}
              rows={d.customers.map((c) => ({
                id: c.customerId,
                name: c.customerName,
                cents: c.cents,
                totalCents: c.totalCents,
              }))}
              foot={{
                id: 'total',
                name: 'Total',
                cents: Object.fromEntries(d.buckets.map((b) => [b.key, b.cents])),
                totalCents: d.totalCents,
              }}
            />
          )}
        </>
      )}
    </Section>
  );
}

type MonthRow = { id: string; month: string; invoices: number; revenueCents: number; total?: boolean };
const REVENUE_COLUMNS: DataColumn<MonthRow>[] = [
  {
    id: 'month',
    label: 'Month',
    rowHeader: true,
    cell: (r) => ({ text: r.total ? 'Total' : monthWords(r.month) }),
  },
  { id: 'invoices', label: 'Invoices', align: 'end', cell: (r) => ({ text: r.invoices }) },
  {
    id: 'revenue',
    label: 'Revenue before tax',
    align: 'end',
    cell: (r) => ({ text: dash(r.revenueCents, !!r.total || !!r.invoices || !!r.revenueCents) }),
  },
];

/** Completed revenue before tax by month, the 18 months through this one (history fix 1). */
function Revenue() {
  const q = useQuery<{
    title: string;
    months: { month: string; invoices: number; revenueCents: number }[];
    totalCents: number;
    undated: { invoices: number; revenueCents: number };
  }>('reports.revenueByMonth', {});
  const csv = useCsv('reports.revenueByMonth', {});
  const d = q.data;
  return (
    <Section title={d?.title ?? 'Revenue by month'}>
      <div className="bp-reports-tools">{csv.link}</div>
      {csv.said}
      {!d ? (
        <Waiting error={q.error} what="the revenue" onRetry={() => void q.reload()} />
      ) : (
        <>
          {/* what it counts, in the words Performance and the huddle use (QA-714) */}
          <Say>
            {REVENUE_WORDS} {BOOKS_INCOME}
          </Say>
          {d.undated.invoices > 0 && (
            <Say>
              {d.undated.invoices} {d.undated.invoices === 1 ? 'invoice has' : 'invoices have'} no date, so{' '}
              {d.undated.invoices === 1 ? 'it’s' : 'they’re'} in no month: {money(d.undated.revenueCents)}{' '}
              before tax.
            </Say>
          )}
          <DataTable
            label={d.title}
            columns={REVENUE_COLUMNS}
            rows={[...d.months].reverse().map((r) => ({ ...r, id: r.month }))}
            foot={{
              id: 'total',
              month: '',
              invoices: d.months.reduce((t, r) => t + r.invoices, 0),
              revenueCents: d.totalCents,
              total: true,
            }}
          />
        </>
      )}
    </Section>
  );
}

interface HoursRow {
  staffId: string;
  name: string;
  regularMinutes: number;
  overtimeMinutes: number;
  doubleMinutes: number;
  statMinutes: number;
  ptoMinutes: number;
  unpaidLeaveMinutes: number;
  holidayMinutes: number;
  totalMinutes: number;
}
const hours = (id: string, label: string, key: keyof HoursRow): DataColumn<HoursRow & { id: string }> => ({
  id,
  label,
  align: 'end',
  cell: (r) => ({ text: hoursText(r[key] as number) }),
});
const TIMESHEET_COLUMNS: DataColumn<HoursRow & { id: string }>[] = [
  { id: 'name', label: 'Technician', rowHeader: true, cell: (r) => ({ text: r.name }) },
  hours('regular', 'Regular', 'regularMinutes'),
  hours('overtime', 'Overtime 1.5×', 'overtimeMinutes'),
  hours('double', 'Double 2×', 'doubleMinutes'),
  hours('stat', 'Stat hours', 'statMinutes'),
  hours('pto', 'Time off (paid)', 'ptoMinutes'),
  hours('unpaid', 'Unpaid leave', 'unpaidLeaveMinutes'),
  hours('holiday', 'Holidays', 'holidayMinutes'),
  hours('total', 'Total paid', 'totalMinutes'),
];

function Timesheets({ tz }: Props) {
  const m = useMonth(tz);
  const input = { from: m.from, to: m.to };
  const q = useQuery<{ from: string; to: string; title: string; rows: HoursRow[] }>(
    'reports.timesheets',
    input,
  );
  const csv = useCsv('reports.timesheets', input);
  const d = q.data;
  return (
    <Section title={d?.title ?? 'Timesheets'}>
      <div className="bp-reports-tools">
        {m.picker}
        {csv.link}
      </div>
      {csv.said}
      {!d ? (
        <Waiting error={q.error} what="the timesheets" onRetry={() => void q.reload()} />
      ) : (
        <>
          {(d.from !== m.from || d.to !== m.to) && (
            <Say>
              Whole payroll weeks, so overtime and stat hours split as payroll does: {dayText(d.from)} to{' '}
              {dayText(d.to)}.
            </Say>
          )}
          {!d.rows.length ? (
            <Say>No hours this month.</Say>
          ) : (
            <DataTable
              label={d.title}
              columns={TIMESHEET_COLUMNS}
              rows={d.rows.map((r) => ({ ...r, id: r.staffId }))}
            />
          )}
        </>
      )}
    </Section>
  );
}

interface PayRow {
  staffId: string;
  name: string;
  regularMinutes: number;
  overtimeMinutes: number;
  doubleMinutes: number;
  statMinutes: number;
  hourlyPayCents?: number;
  salaryCents?: number;
  commissionsCents?: number;
  spiffsCents?: number;
  leaveCents?: number;
  grossCents?: number;
}
type PaysRow = PayRow & { id: string; total?: boolean };
/** The total says only the gross, under the hours and the pays. */
const payHours = (id: string, label: string, key: keyof PayRow): DataColumn<PaysRow> => ({
  id,
  label,
  align: 'end',
  cell: (r) => ({ text: r.total ? '' : hoursText(r[key] as number) }),
});
const pay = (id: string, label: string, key: keyof PayRow): DataColumn<PaysRow> => ({
  id,
  label,
  align: 'end',
  cell: (r) => ({ text: r.total && key !== 'grossCents' ? '' : money((r[key] as number | undefined) ?? 0) }),
});
const PAY_COLUMNS: DataColumn<PaysRow>[] = [
  { id: 'name', label: 'Name', rowHeader: true, cell: (r) => ({ text: r.name }) },
  payHours('regular', 'Regular h', 'regularMinutes'),
  payHours('overtime', 'OT h', 'overtimeMinutes'),
  payHours('double', 'Double h', 'doubleMinutes'),
  payHours('stat', 'Stat h', 'statMinutes'),
  pay('hourly', 'Hourly pay', 'hourlyPayCents'),
  pay('salary', 'Salary', 'salaryCents'),
  pay('commissions', 'Commissions', 'commissionsCents'),
  pay('spiffs', 'Spiffs', 'spiffsCents'),
  pay('leave', 'Stat & sick', 'leaveCents'),
  pay('gross', 'Gross', 'grossCents'),
];

function Pays() {
  const periods = useQuery<{ periods: { id: string; startDate: string; endDate: string; status: string }[] }>(
    'payPeriods.list',
    {},
  );
  const [chosen, setChosen] = useState<string | null>(null);
  const id = chosen ?? periods.data?.periods[0]?.id ?? null;
  const q = useQuery<{
    title: string;
    prepared: boolean;
    stale: boolean;
    rows: PayRow[];
    total: { grossCents?: number };
  }>('reports.totalPays', id ? { periodId: id } : null);
  const csv = useCsv('reports.totalPays', { periodId: id });
  if (!periods.data || !periods.data.periods.length)
    return (
      <Section title="Total pays">
        {!periods.data ? (
          <Waiting error={periods.error} what="the pay periods" onRetry={() => void periods.reload()} />
        ) : (
          <Say>No pay periods yet.</Say>
        )}
      </Section>
    );
  const d = q.data;
  return (
    <Section title={d?.title ?? 'Total pays'}>
      <div className="bp-reports-tools">
        <SelectField
          label="Pay period"
          value={id ?? ''}
          onChange={setChosen}
          width="fit"
          options={periods.data.periods.map((p) => ({
            value: p.id,
            label: `${dayText(p.startDate)} – ${dayText(p.endDate)}`,
          }))}
        />
        {id && csv.link}
      </div>
      {csv.said}
      {!d ? (
        <Waiting error={q.error} what="the pays" onRetry={() => void q.reload()} />
      ) : !d.prepared ? (
        <Say>This period hasn’t been prepared yet: prepare it in Books › Payroll first.</Say>
      ) : (
        <>
          {d.stale && <Notice title="Hours were corrected since it was prepared." />}
          <DataTable
            label={d.title}
            columns={PAY_COLUMNS}
            rows={d.rows.map((r) => ({ ...r, id: r.staffId }))}
            foot={{
              id: 'total',
              staffId: '',
              name: 'Total',
              regularMinutes: 0,
              overtimeMinutes: 0,
              doubleMinutes: 0,
              statMinutes: 0,
              grossCents: d.total.grossCents ?? 0,
              total: true,
            }}
          />
        </>
      )}
    </Section>
  );
}

interface ReasonRow {
  key: string | null;
  label: string;
  taken: number;
  booked: number;
  leads: number;
  unbookedLeads: number;
  excused: number;
  underAMinute: number;
  notSet: number;
  rate: number | null;
}
const count = (id: string, label: string, key: keyof ReasonRow): DataColumn<ReasonRow & { id: string }> => ({
  id,
  label,
  align: 'end',
  cell: (r) => ({ text: r[key] as number }),
});
const REASON_COLUMNS: DataColumn<ReasonRow & { id: string }>[] = [
  { id: 'reason', label: 'Reason', rowHeader: true, cell: (r) => ({ text: r.label }) },
  count('taken', 'Taken', 'taken'),
  count('booked', 'Booked', 'booked'),
  count('leads', 'Leads', 'leads'),
  count('unbooked', 'Unbooked leads', 'unbookedLeads'),
  count('excused', 'Excused', 'excused'),
  count('short', 'Under a minute', 'underAMinute'),
  count('notset', 'Not set', 'notSet'),
  { id: 'rate', label: 'Booking rate', align: 'end', cell: (r) => ({ text: rateText(r.rate) }) },
];

function Calls({ tz }: Props) {
  const m = useMonth(tz);
  const input = { from: m.from, to: m.to };
  const q = useQuery<{ rows: ReasonRow[]; total: ReasonRow; missed: number }>('reports.callReasons', input);
  const csv = useCsv('reports.callReasons', input);
  const d = q.data;
  return (
    <Section title={`Calls by reason · ${monthName(m.from)}`}>
      <div className="bp-reports-tools">
        {m.picker}
        {csv.link}
      </div>
      {csv.said}
      {!d ? (
        <Waiting error={q.error} what="the calls" onRetry={() => void q.reload()} />
      ) : !d.rows.length ? (
        <Say>No calls this month.</Say>
      ) : (
        <>
          {d.missed > 0 && <Say>{d.missed} missed or voicemail, taken by nobody.</Say>}
          <DataTable
            label={`Calls by reason, ${monthName(m.from)}`}
            columns={REASON_COLUMNS}
            rows={d.rows.map((r) => ({ ...r, id: r.key ?? r.label }))}
            foot={{ ...d.total, id: 'total', label: 'Total' }}
          />
        </>
      )}
    </Section>
  );
}

interface CampaignRow {
  key: string | null;
  label: string;
  channel: string | null;
  month: string | null;
  calls: number;
  leads: number;
  unbookedLeads: number;
  booked: number;
  leadConversionRate: number | null;
  callsBooked: number;
  callBookingRate: number | null;
  jobsCompleted: number;
  invoicedJobs: number;
  revenueCents: number;
  averageTicketCents: number | null;
  /** Only for whoever sees margins (finance.margins.view); read from the work while mirroring (st-mirror §7). */
  grossProfitCents?: number | null;
  /** BL-008's ad spend in the period, each entry spread evenly over its days; estimated when part of
   *  it is a share of an imported total spread over a longer period. */
  spendCents: number;
  spendEstimated: boolean;
  costPerLeadCents: number | null;
  costPerBookedJobCents: number | null;
  roas: number | null;
}
type CampaignsRow = CampaignRow & { id: string; total?: boolean };

/** reports.campaigns' columns for a grouping: the names it groups by, then the counts, money and cost. */
function campaignColumns(groupBy: CampaignGroup, margins: boolean): DataColumn<CampaignsRow>[] {
  const showChannel = groupBy === 'campaign' || groupBy === 'campaign_month';
  const n = (id: string, label: string, key: keyof CampaignRow): DataColumn<CampaignsRow> => ({
    id,
    label,
    align: 'end',
    cell: (r) => ({ text: r[key] as number }),
  });
  const rate = (id: string, label: string, key: keyof CampaignRow): DataColumn<CampaignsRow> => ({
    id,
    label,
    align: 'end',
    cell: (r) => ({ text: rateText(r[key] as number | null) }),
  });
  const cost = (id: string, label: string, key: keyof CampaignRow): DataColumn<CampaignsRow> => ({
    id,
    label,
    align: 'end',
    cell: (r) => ({ text: dash(r[key] as number | null, r[key] !== null) }),
  });
  return [
    {
      id: 'name',
      label: groupBy === 'channel' ? 'Channel' : groupBy === 'month' ? 'Month' : 'Campaign',
      rowHeader: true,
      cell: (r) => ({ text: r.total ? 'Total' : campaignRowLabel(r, groupBy) }),
    },
    ...(showChannel
      ? [
          {
            id: 'channel',
            label: 'Channel',
            cell: (r: CampaignsRow) => ({
              text: r.total ? '' : r.channel ? (CHANNEL_WORDS[r.channel as Channel] ?? r.channel) : '—',
            }),
          },
        ]
      : []),
    ...(groupBy === 'campaign_month'
      ? [
          {
            id: 'month',
            label: 'Month',
            nowrap: true,
            cell: (r: CampaignsRow) => ({ text: r.total ? '' : monthWords(r.month) }),
          },
        ]
      : []),
    n('calls', 'Calls', 'calls'),
    n('leads', 'Leads', 'leads'),
    // the other half of both rates, so "40 %" reads as 2 of 5 or 40 of 100 (books)
    n('unbooked', 'Unbooked leads', 'unbookedLeads'),
    n('booked', 'Jobs booked', 'booked'),
    rate('callsBooked', 'Calls booked', 'callBookingRate'),
    rate('converted', 'Leads that became jobs', 'leadConversionRate'),
    n('completed', 'Completed', 'jobsCompleted'),
    { id: 'revenue', label: 'Revenue', align: 'end', cell: (r) => ({ text: money(r.revenueCents) }) },
    // "per job" folded into the label (Seth, 5 Nov): a head is one line
    cost('ticket', 'Average ticket per invoiced job', 'averageTicketCents'),
    // what it cost (BL-008): the spend, then what each lead and booked job cost, and revenue per dollar
    {
      id: 'spend',
      label: 'Ad spend',
      align: 'end',
      cell: (r) => ({
        text:
          r.spendCents || r.spendEstimated ? (
            <>
              {money(r.spendCents)}
              {r.spendEstimated && <span className="bp-reports-est"> est.</span>}
            </>
          ) : (
            '—'
          ),
      }),
    },
    cost('perLead', 'Cost per lead', 'costPerLeadCents'),
    cost('perJob', 'Cost per booked job', 'costPerBookedJobCents'),
    {
      id: 'roas',
      label: 'Return on ad spend, revenue per $1',
      align: 'end',
      cell: (r) => ({ text: roasText(r.roas) }),
    },
    ...(margins
      ? [
          {
            id: 'gp',
            label: 'Gross profit',
            align: 'end' as const,
            cell: (r: CampaignsRow) => ({
              text: r.grossProfitCents == null ? '—' : money(r.grossProfitCents),
            }),
          },
        ]
      : []),
  ];
}

/** reports.campaigns (e079d7fc, BL-008): a month or a year, by campaign, channel, month or both. */
function Campaigns({ tz }: Props) {
  const [[monthFrom, monthTo], setMonth] = useState(monthOf(todayIn(tz)));
  const [wholeYear, setWholeYear] = useState(false);
  const [groupBy, setGroupBy] = useState<CampaignGroup>('campaign');
  const [from, to] = wholeYear ? yearOf(monthFrom) : [monthFrom, monthTo];
  const input = { from, to, groupBy };
  const q = useQuery<{
    rows: CampaignRow[];
    total: CampaignRow;
    grossProfitNote: string | null;
    /** Said only while no ad spend is recorded for the period. */
    adSpendNote: string | null;
  }>('reports.campaigns', input);
  const csv = useCsv('reports.campaigns', input);
  const step = (by: 1 | -1) => {
    let r = stepMonth(monthFrom, by);
    if (wholeYear) for (let i = 1; i < 12; i++) r = stepMonth(r[0], by);
    setMonth(r);
  };
  const period = wholeYear ? monthFrom.slice(0, 4) : monthName(monthFrom);
  const d = q.data;
  // the gross profit column only for whoever the server gave it to
  const margins = !!d && 'grossProfitCents' in d.total;
  return (
    <Section title={`Campaigns · ${period}`}>
      <div className="bp-reports-tools">
        <div className="bp-reports-step" role="group" aria-label={wholeYear ? 'Year' : 'Month'}>
          <IconButton
            size="sm"
            icon="chevron-left"
            label={wholeYear ? 'Previous year' : 'Previous month'}
            onPress={() => step(-1)}
          />
          <span className="bp-reports-now">{period}</span>
          <IconButton
            size="sm"
            icon="chevron-right"
            label={wholeYear ? 'Next year' : 'Next month'}
            onPress={() => step(1)}
          />
        </div>
        <Segmented
          label="Period"
          items={[
            { id: 'month', label: 'Month' },
            { id: 'year', label: 'Year' },
          ]}
          selectedKey={wholeYear ? 'year' : 'month'}
          onSelectionChange={(p) => setWholeYear(p === 'year')}
        />
        <SelectField
          label="Group"
          value={groupBy}
          width="fit"
          onChange={(g) => setGroupBy(g as CampaignGroup)}
          options={CAMPAIGN_GROUPS.map(([g, words]) => ({ value: g, label: words }))}
        />
        {csv.link}
      </div>
      {csv.said}
      {!d ? (
        <Waiting error={q.error} what="the campaigns" onRetry={() => void q.reload()} />
      ) : !d.rows.length ? (
        <Say>No calls, leads or jobs {wholeYear ? 'this year' : 'this month'}.</Say>
      ) : (
        <>
          {(d.adSpendNote || d.grossProfitNote) && (
            <Say>{[d.adSpendNote, d.grossProfitNote].filter(Boolean).join(' ')}</Say>
          )}
          <DataTable
            label={`Campaigns, ${period}`}
            columns={campaignColumns(groupBy, margins)}
            rows={d.rows.map((r) => ({ ...r, id: `${r.key ?? 'none'}:${r.month ?? ''}` }))}
            foot={{ ...d.total, id: 'total', total: true }}
          />
          {(d.total.spendEstimated || d.rows.some((r) => r.spendEstimated)) && (
            <Say>
              est.: a share of a total spread over a longer period (an imported history), so an estimate.
            </Say>
          )}
        </>
      )}
    </Section>
  );
}

export function ReportsPage({
  nav,
  held,
  start,
}: {
  nav: NavProps;
  held: readonly string[];
  start?: Report;
}) {
  const mine = reportsFor(held);
  const [report, setReport] = useState<Report>(start ?? mine[0]?.report ?? 'aging');
  const here = mine.find((r) => r.report === report) ?? mine[0];
  const tz = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <div className="bp-reports">
          <PageHead
            {...(crumb ? { crumb } : {})}
            title={here?.label ?? 'Reports'}
            {...(here ? { lede: here.lede } : {})}
            tabs={{
              label: 'Reports',
              items: mine.map((r) => ({
                label: r.label,
                isCurrent: here?.report === r.report,
                onPress: () => setReport(r.report),
              })),
            }}
          />
          {here?.report === 'payments' && <Payments tz={tz} />}
          {here?.report === 'aging' && <AgingReport tz={tz} />}
          {here?.report === 'revenue' && <Revenue />}
          {here?.report === 'timesheets' && <Timesheets tz={tz} />}
          {here?.report === 'pays' && <Pays />}
          {here?.report === 'calls' && <Calls tz={tz} />}
          {here?.report === 'campaigns' && <Campaigns tz={tz} />}
        </div>
      </main>
    </div>
  );
}
